/** Mobile bottom navigation (v1 mobile-nav.js) — a native-app style fixed bottom
 * bar for phones: four primary destinations + a "More" sheet. Desktop hides it
 * via CSS.
 *
 * The sheet is SEGMENTED (2026-07-29, Tom: "way too clutter and hard to find
 * things ... on the website it divides to 3 sections"). It used to render all
 * ~30 pages as one flat two-column grid of bare text labels — no grouping, no
 * icons, and no badges, so 285 unread and 26 open tasks were invisible until
 * you opened the page. Now the same Workspace / Finances / Admin split the
 * sidebar uses, one group at a time, so nothing is more than two taps away and
 * no group is long enough to scroll off.
 *
 * The groups are passed IN from the sidebar's own arrays rather than duplicated
 * here. The old copy had already drifted: it listed Dialer (retired — merged
 * into Inbox), and carried Investors and Notifications that the sidebar never
 * had. One source, so the two navs cannot diverge again.
 */
import { useState } from "react";
import { Icon, type IconName } from "../shell/icons";

export interface NavItem {
  key: string;
  label: string;
  icon: IconName;
  badge?: string | null;
  hot?: boolean;
}
export interface NavGroup { title: string; items: NavItem[] }
export interface NavExtra { label: string; onClick: () => void }

const PRIMARY = ["dashboard", "properties", "deals", "tasks"] as const;
const SHORT: Record<string, string> = { dashboard: "Home", properties: "Properties", deals: "Deals", tasks: "Tasks" };
const ICON: Record<string, IconName> = { dashboard: "dashboard", properties: "building", deals: "deals", tasks: "tasks" };

export function BottomNav({ page, onNav, groups, extras = [] }: {
  page: string;
  onNav: (p: string) => void;
  groups: NavGroup[];
  extras?: NavExtra[];
}) {
  const [more, setMore] = useState(false);
  const [seg, setSeg] = useState(0);

  const shown = groups.filter((g) => g.items.length);
  // Which segment the current page lives in — opening the sheet should land you
  // where you already are, not always on Workspace.
  const openSheet = () => {
    const i = shown.findIndex((g) => g.items.some((it) => it.key === page));
    setSeg(i < 0 ? 0 : i);
    setMore(true);
  };

  const visible = new Set(shown.flatMap((g) => g.items.map((i) => i.key)));
  const primary = PRIMARY.filter((k) => visible.has(k));
  const go = (k: string) => { onNav(k); setMore(false); };
  const runExtra = (fn: () => void) => { setMore(false); fn(); };
  const active = shown[Math.min(seg, shown.length - 1)];

  return (
    <>
      {more ? (
        <div className="bnsheetscrim" onClick={() => setMore(false)}>
          <div className="bnsheet" onClick={(e) => e.stopPropagation()}>
            <div className="bnseg" role="tablist">
              {shown.map((g, i) => (
                <button
                  key={g.title}
                  role="tab"
                  aria-selected={i === seg}
                  className={"bnsegbtn" + (i === seg ? " on" : "")}
                  onClick={() => setSeg(i)}
                >
                  {g.title}
                </button>
              ))}
            </div>
            <div className="bnsheetgrid">
              {(active?.items ?? []).map((it) => (
                <button
                  key={it.key}
                  className={"bnsheetitem" + (page === it.key ? " active" : "")}
                  onClick={() => go(it.key)}
                >
                  <Icon name={it.icon} size={17} className="bnsheetico" />
                  <span className="bnsheetlbl">{it.label}</span>
                  {it.badge ? <span className={"bnsheetbadge" + (it.hot ? " hot" : "")}>{it.badge}</span> : null}
                </button>
              ))}
            </div>
            {extras.length ? (
              <div className="bnsheetextras">
                {extras.map((x) => <button key={x.label} className="bnsheetextra" onClick={() => runExtra(x.onClick)}>{x.label}</button>)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <nav className="bottomnav">
        {primary.map((k) => (
          <button key={k} className={"bnbtn" + (page === k ? " active" : "")} onClick={() => go(k)}>
            <Icon name={ICON[k] ?? "dashboard"} size={20} className="bnico" />
            <span className="bnlbl">{SHORT[k]}</span>
          </button>
        ))}
        <button className={"bnbtn" + (more ? " active" : "")} onClick={() => (more ? setMore(false) : openSheet())}>
          <Icon name="menu" size={20} className="bnico" />
          <span className="bnlbl">More</span>
        </button>
      </nav>
    </>
  );
}
